import { FormConfig, useTranslation } from '@ibe/components';
import fallback from '../Translations/generated/traveller-builder.de.json';
import Keys from '../Translations/generated/traveller-builder.de.json.keys';
import { ConfigModel } from '@ibe/services';

export const getTravellerMockData = (): Record<string, string> => {
  return {
    salutation: 'male',
    firstname: 'ISO',
    lastname: 'Test',
    nationality: 'DE',
    birthDate: '01-02-1972',
    street: 'Zum Laurenburger Hof',
    city: 'Frankfurt',
    zipCode: '60594',
    country: 'DE',
    phone: '0911995940',
    email: 'isojupiter@gmail.com'
  };
};

export interface FormItemsProps {
  birthDate: string;
  city: string;
  country: string;
  email: string;
  firstname: string;
  id: string;
  lastname: string;
  nationality: string;
  phone: string;
  salutation: string;
  street: string;
  zipCode: string;

  [a: string]: string;
}

const useTravellerFormConfigProductIBE = (
  isFirstItem: boolean,
  initialValues?: { [key: string]: string },
  config?: ConfigModel,
  idx?: number
): FormConfig => {
  const { t } = useTranslation('traveller-builder', fallback);
  const maxDate = new Date();
  if (config?.traveler.maxChildAge) {
    maxDate.setFullYear(maxDate.getFullYear() - (config.traveler.maxChildAge + 1));
  }

  const zipValidationDE = {
    type: 'zipCode',
    customMessage: t(Keys.zipCodeError)
  };

  const zipValidationAT = {
    type: 'customRegExValidation',
    parameter: /^\d{4}$/,
    customMessage: t(Keys.zipCodeError)
  };

  const zipValidationCH = {
    type: 'customRegExValidation',
    parameter: /^\d{4}$/,
    customMessage: t(Keys.zipCodeError)
  };

  const getZipValidation = () => {
    if (config?.tenant === 'AT') return [zipValidationAT];
    if (config?.tenant === 'CH') return [zipValidationCH];
    return [zipValidationDE];
  };

  const zipValidation = getZipValidation();

  return {
    rows: [
      {
        key: 'row1',
        elements: [
          {
            id: idx?.toString(),
            tag: 'select',
            key: 'salutation',
            // autoComplete: 'honorific-prefix',
            label: t(Keys.salutation),
            placeholder: ' ',
            initialValue: initialValues?.salutation ?? '',
            externalDataOptions: 'salutations',
            isSearchable: false,
            rowGrid: {
              md: 6
            },
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                }
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true
          },
          {
            id: idx?.toString(),
            tag: 'input',
            key: 'title',
            autoComplete: 'honorific-prefix',
            label: t(Keys.title),
            placeholder: ' ',
            initialValue: initialValues?.title ?? '',
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'customRegExValidation',
                  parameter: /^(?:\p{Letter}[\p{Letter}\p{Mark}'\-/().]*(?: [\p{Letter}\p{Mark}'\-/().]+)*)?$/u,
                  customMessage: t(Keys.titleCityError)
                }
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true,
            rowGrid: {
              md: 6
            }
          },
          {
            id: idx?.toString(),
            tag: 'input',
            key: 'firstname',
            autoComplete: 'given-name',
            label: t(Keys.firstname),
            placeholder: ' ',
            initialValue: initialValues?.firstname ?? '',
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                },
                {
                  type: 'customRegExValidation',
                  parameter: /^(?!.*[()<>/\\=:?])\p{Letter}[\p{Letter}\p{Mark}'-]*(?: [\p{Letter}\p{Mark}'-]+)*$/u,
                  customMessage: t(Keys.nameError)
                }
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true,
            rowGrid: {
              md: 6
            }
          },
          {
            id: idx?.toString(),
            tag: 'input',
            key: 'lastname',
            autoComplete: 'family-name',
            label: t(Keys.lastname),
            placeholder: ' ',
            initialValue: initialValues?.lastname ?? '',
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                },
                {
                  type: 'customRegExValidation',
                  parameter: /^(?!.*[()<>/\\=:?])\p{Letter}[\p{Letter}\p{Mark}'-]*(?: [\p{Letter}\p{Mark}'-]+)*$/u,
                  customMessage: t(Keys.nameError)
                }
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true,
            rowGrid: {
              md: 6
            }
          },
          {
            id: idx?.toString(),
            tag: 'datePicker',
            key: 'birthDate',
            autoComplete: 'bday',
            label: t(Keys.dateOfBirth),
            placeholder: ' ',
            initialValue: initialValues?.birthDate ?? '',
            withKeyboardSupport: true,
            displayFormat: 'DD.MM.YYYY',
            maxDate: isFirstItem ? maxDate : new Date(),
            inputClassName: 'form-control',
            useFormValidation: true,
            setValueToNullWhenFormatIsIncorrect: true,
            rowGrid: {
              md: 6
            },
            validations: {
              schema: 'string',
              types: [
                ...[
                  {
                    type: 'shouldNotBeNull',
                    customMessage: t(Keys.shouldNotBeNull)
                  },
                  {
                    type: 'isBirthDateYoungerThanMaxAgeOnCurrentDate',
                    parameter: 120,
                    customMessage: t(Keys.mustBeLowerThan)
                  }
                ],
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                },
                ...(isFirstItem
                  ? [
                      {
                        type: 'isAdultOnCurrentDate',
                        customMessage: t(Keys.mustBeAdult)
                      }
                    ]
                  : [
                      {
                        type: 'isBirthDateOlderThanMinAgeOnStartDate',
                        parameter: 16,
                        customMessage: t(Keys.mustBeOlderThan)
                      }
                    ])
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true
          },
          {
            id: idx?.toString(),
            tag: 'select',
            key: 'nationality',
            // autoComplete: 'country',
            label: t(Keys.nationality),
            placeholder: ' ',
            initialValue: initialValues?.nationality ?? '',
            externalDataOptions: 'nationalities',
            isSearchable: false,
            rowGrid: {
              md: 6
            },
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                }
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true
          },
          {
            id: idx?.toString(),
            tag: 'external',
            key: 'sameAddressCheckbox',
            childIndex: 1,
            rowGrid: {
              xs: 0,
              md: 12
            },
            hideOnExternalChange: {
              property: 'isFirstAdult'
            }
          },
          {
            id: idx?.toString(),
            tag: 'inputSearchDropdown',
            className: 'traveler-street',
            key: 'street',
            autoComplete: 'street-address',
            label: t(Keys.street_houseNumber),
            initialValue: initialValues?.street ?? '',
            placeholder: ' ',
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                }
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true,
            rowGrid: {
              md: 6
            },
            hideOnExternalChange: {
              property: 'hideFields'
            },
            externalDataOptions: 'googlePlacesResults'
          },
          {
            key: 'external4',
            id: idx?.toString(),
            tag: 'external',
            childIndex: 0,
            rowGrid: {
              xs: 0,
              md: 6
            },
            hideOnExternalChange: {
              property: 'hideFields'
            }
          },
          {
            id: idx?.toString(),
            tag: 'input',
            key: 'zipCode',
            label: t(Keys.zipCode),
            autoComplete: 'postal-code',
            placeholder: ' ',
            initialValue: initialValues?.zipCode ?? '',
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                },
                ...zipValidation
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true,
            rowGrid: {
              md: 6
            },
            hideOnExternalChange: {
              property: 'hideFields'
            }
          },
          {
            id: idx?.toString(),
            tag: 'input',
            key: 'city',
            autoComplete: 'address-level2',
            label: t(Keys.city),
            placeholder: ' ',
            initialValue: initialValues?.city ?? '',
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                },
                {
                  type: 'customRegExValidation',
                  parameter: /^\p{Letter}[\p{Letter}\p{Mark}'\-/()]*(?: [\p{Letter}\p{Mark}'\-/()]+)*$/u,
                  customMessage: t(Keys.titleCityError)
                }
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true,
            rowGrid: {
              md: 6
            },
            hideOnExternalChange: {
              property: 'hideFields'
            }
          },
          {
            id: idx?.toString(),
            tag: 'select',
            key: 'country',
            autoComplete: 'country-name',
            label: t(Keys.country),
            placeholder: ' ',
            initialValue: initialValues?.country ?? '',
            externalDataOptions: 'countries',
            isSearchable: false,
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                }
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true,
            rowGrid: {
              md: 6
            },
            hideOnExternalChange: {
              property: 'hideFields'
            }
          },
          {
            key: 'external2',
            id: idx?.toString(),
            tag: 'external',
            childIndex: 0,
            rowGrid: {
              xs: 0,
              md: 6
            },
            hideOnExternalChange: {
              property: 'hideFields'
            }
          },
          {
            id: idx?.toString(),
            tag: 'input',
            key: 'phone',
            autoComplete: 'tel',
            label: t(Keys.phone),
            placeholder: ' ',
            initialValue: initialValues?.phone ?? '',
            validations: {
              schema: 'string',
              types: [
                {
                  type: 'required',
                  customMessage: t(Keys.inputRequired)
                },
                {
                  type: 'customRegExValidation',
                  parameter: /^[-+()0-9/]+$/,
                  customMessage: t(Keys.phoneError)
                }
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true,
            rowGrid: {
              md: 6
            }
          },
          {
            id: idx?.toString(),
            tag: 'input',
            key: 'email',
            autoComplete: 'email',
            label: isFirstItem ? t(Keys.email) : t(Keys.emailOptional),
            initialValue: initialValues?.email ?? '',
            placeholder: ' ',
            validations: {
              schema: 'string',
              types: [
                ...(isFirstItem
                  ? [
                      {
                        type: 'required',
                        customMessage: t(Keys.inputRequired)
                      },
                      {
                        type: 'customRegExValidation',
                        parameter: /^[_a-zA-Z0-9\-\.\+]+@([a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)+)$/,
                        customMessage: t(Keys.emailError)
                      }
                    ]
                  : [
                      {
                        type: 'customRegExValidation',
                        parameter: /^$|^[_a-zA-Z0-9\-\.\+]+@([a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)+)$/,
                        customMessage: t(Keys.emailError)
                      }
                    ])
              ]
            },
            validateOnBlurOnly: true,
            showSuccessState: true,
            rowGrid: {
              md: 6
            }
          },
          {
            key: 'external3',
            id: idx?.toString(),
            tag: 'external',
            childIndex: 0,
            rowGrid: {
              xs: 0,
              md: 2
            }
          }
        ]
      }
    ]
  };
};
export default useTravellerFormConfigProductIBE;
